import { Link } from 'react-router-dom'
import LangSwitch from '../components/LangSwitch'
import { useT } from '../i18n'
import { ERAS } from '../lib/eras'
import { fmtYear } from '../lib/format'

const HERO_SRC = '/hero.jpg'

export default function Game() {
  const { t, locale } = useT()

  return (
    <main className="bg-cream-50 text-earth-900">
      {/* HERO ----------------------------------------------------------------- */}
      <section className="relative min-h-screen overflow-hidden text-cream-50">
        {/* Photo backdrop */}
        <div className="absolute inset-0">
          <img
            src={HERO_SRC}
            alt=""
            className="absolute inset-0 w-full h-full object-cover eg-hero-pan"
            loading="eager"
          />
          {/* Soft warm wash — lets the photo's golden hour come through but
              tames the lower half for text legibility. Two stacked layers. */}
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(180deg, rgba(20, 12, 5, 0.20) 0%, rgba(20, 12, 5, 0.05) 40%, rgba(35, 22, 10, 0.55) 75%, rgba(20, 12, 5, 0.85) 100%)',
            }}
          />
          {/* Subtle warm vignette */}
          <div
            className="absolute inset-0"
            style={{
              background:
                'radial-gradient(ellipse at center, transparent 35%, rgba(20, 12, 5, 0.45) 100%)',
            }}
          />
          {/* Very light film grain */}
          <div className="absolute inset-0 eg-grain opacity-15 mix-blend-overlay pointer-events-none" />
        </div>

        {/* Top nav */}
        <header className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pt-6 sm:pt-8 flex items-center justify-between text-cream-50/85">
          <div className="flex items-baseline gap-3 text-xs uppercase tracking-[0.3em]">
            <span aria-hidden className="inline-block w-6 eg-rule h-px" />
            <span>{t('game.kicker')}</span>
          </div>
          <LangSwitch className="!text-cream-50/85 hover:!text-white" />
        </header>

        {/* Hero content */}
        <div className="relative z-10 max-w-3xl mx-auto px-6 pt-20 sm:pt-28 pb-32 text-center">
          <h1 className="font-display font-bold tracking-tight leading-[0.95] text-[clamp(4rem,12vw,8.5rem)] drop-shadow-[0_2px_18px_rgba(0,0,0,0.45)]">
            {t('game.title')}
          </h1>

          <div className="mx-auto mt-6 sm:mt-8 w-32 eg-rule" />

          <p className="font-body italic font-light mt-6 sm:mt-8 text-xl sm:text-3xl leading-snug text-cream-50/95 drop-shadow-[0_1px_8px_rgba(0,0,0,0.4)]">
            {t('game.tagline')}
          </p>

          <p className="font-body font-normal mt-6 sm:mt-8 text-base sm:text-lg leading-relaxed tracking-wide text-cream-100/90 max-w-2xl mx-auto drop-shadow-[0_1px_6px_rgba(0,0,0,0.35)]">
            {t('game.description')}
          </p>

          <div className="mt-10 sm:mt-12">
            <Link
              to="/play"
              className="inline-block bg-sand-500 text-earth-900 rounded-md px-9 sm:px-11 py-3.5 sm:py-4 text-base sm:text-lg font-semibold tracking-wide hover:bg-sand-300 transition shadow-[0_6px_28px_rgba(0,0,0,0.4)] hover:shadow-[0_8px_36px_rgba(0,0,0,0.5)] hover:-translate-y-0.5 duration-200"
            >
              {t('game.play_cta')}
            </Link>
          </div>

          <p className="mt-6 text-[11px] tracking-[0.25em] uppercase text-cream-100/65">
            {new Date().toLocaleDateString(locale === 'ar' ? 'ar-EG' : 'en-US', {
              year: 'numeric', month: 'long', day: 'numeric',
            })}
          </p>
        </div>
      </section>

      {/* HOW IT WORKS --------------------------------------------------------- */}
      <section className="bg-cream-50 py-20 sm:py-28 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14 sm:mb-20">
            <div className="mx-auto w-24 eg-rule mb-6" />
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-earth-900">
              {t('game.how_title')}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-10 sm:gap-14">
            {([
              ['game.how_1_kicker', 'game.how_1_title', 'game.how_1_body'],
              ['game.how_2_kicker', 'game.how_2_title', 'game.how_2_body'],
              ['game.how_3_kicker', 'game.how_3_title', 'game.how_3_body'],
            ] as const).map(([k, title, body]) => (
              <div key={title} className="text-start">
                <div className="font-display text-3xl text-sand-600 mb-2 tabular-nums">
                  {t(k)}
                </div>
                <div className="w-8 eg-rule mb-4" />
                <h3 className="font-display text-2xl sm:text-[1.7rem] mb-3 leading-tight text-earth-900">
                  {t(title)}
                </h3>
                <p className="font-body text-earth-700 leading-relaxed text-[1.05rem]">
                  {t(body)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ERA TIMELINE --------------------------------------------------------- */}
      <section className="bg-cream-100 py-20 sm:py-28 px-6 border-y border-cream-200">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <div className="mx-auto w-24 eg-rule mb-6" />
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-earth-900">
              {t('game.eras_title')}
            </h2>
            <p className="mt-4 font-body italic text-earth-700/80 text-lg">
              {t('game.eras_sub')}
            </p>
          </div>

          <div className="space-y-3 sm:space-y-4">
            {ERAS.map((era) => {
              const label = locale === 'ar' ? era.label_ar : era.label_en
              return (
                <div
                  key={era.key}
                  className="flex items-stretch gap-4 sm:gap-5 bg-cream-50/80 border border-cream-200 rounded-md p-3 sm:p-4 hover:bg-cream-50 transition"
                >
                  <div className="w-1.5 sm:w-2 rounded-full" style={{ background: era.color }} />
                  <div className="flex-1 flex items-baseline justify-between gap-3">
                    <div className="font-display text-lg sm:text-xl text-earth-900 leading-tight">
                      {label}
                    </div>
                    <div
                      className="text-xs sm:text-sm text-earth-700/70 tabular-nums font-mono whitespace-nowrap"
                      dir="ltr"
                    >
                      {fmtYear(era.start, locale)} – {fmtYear(era.end - 1, locale)}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* FOOTER --------------------------------------------------------------- */}
      <footer className="bg-earth-900 text-cream-200/70 py-12 sm:py-16 px-6">
        <div className="max-w-4xl mx-auto text-sm space-y-3 text-center">
          <div className="mx-auto w-16 eg-rule mb-6 opacity-60" />
          <p className="font-body text-cream-100/85 leading-relaxed">
            {t('game.footer_credit')}
          </p>
          <p className="text-xs text-cream-200/50">
            {t('game.footer_copy')} · {new Date().getFullYear()}
          </p>
          <p className="pt-4">
            <Link to="/curate" className="text-xs text-cream-200/55 hover:text-cream-50 underline">
              {t('game.curator_link')}
            </Link>
          </p>
        </div>
      </footer>
    </main>
  )
}
